// Admin Donors Management Page
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import DataTable from '../../components/admin/DataTable';
import LoadingSpinner from '../../components/admin/LoadingSpinner';
import GlassCard from '../../components/admin/GlassCard';

const AdminDonors: React.FC = () => {
  const location = useLocation();
  const [donors, setDonors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedDonor, setSelectedDonor] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);

  const fetchDonors = useCallback(async () => {
    try {
      const url = filter === 'all'
        ? 'http://localhost:5000/api/donations'
        : `http://localhost:5000/api/donations?status=${filter}`;
      
      const response = await fetch(url);
      const data = await response.json();
      setDonors(data.donations || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching donors:', error);
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchDonors();
  }, [fetchDonors]);

  // Open donor from notification
  useEffect(() => {
    if (location.state?.openDonor) {
      viewDonor(location.state.openDonor);
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const updateStatus = async (id: number, status: string) => {
    try {
      console.log('Updating donor status:', id, status);
      const response = await fetch(`http://localhost:5000/api/donations/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      
      const result = await response.json();
      console.log('Status update response:', result);
      
      if (response.ok) {
        console.log('Status updated successfully, refreshing donors list...');
        await fetchDonors();
        console.log('Donors list refreshed');
      } else {
        console.error('Failed to update status:', result);
        alert('Failed to update status: ' + result.message);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating status. Please check console for details.');
    }
  };

  const viewDonor = async (donor: any) => {
    setSelectedDonor(donor);
    setShowModal(true);
  };

  const columns = [
    { key: 'full_name', label: 'Donor Name', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { 
      key: 'amount', 
      label: 'Amount', 
      sortable: true,
      render: (value: number) => `$${parseFloat(value.toString()).toFixed(2)}`
    },
    {
      key: 'donation_type',
      label: 'Type',
      render: (value: string) => (
        <span style={{
          padding: '4px 8px',
          borderRadius: '6px',
          fontSize: '11px',
          fontWeight: '600',
          textTransform: 'capitalize',
          background: value === 'monthly' ? '#DBEAFE' : '#FEF3C7',
          color: value === 'monthly' ? '#1E40AF' : '#92400E',
          border: value === 'monthly' ? '1px solid #BFDBFE' : '1px solid #FDE68A'
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'payment_method',
      label: 'Payment',
      render: (value: string) => value || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Not specified</span>
    },
    {
      key: 'status',
      label: 'Status',
      render: (value: string) => (
        <span
          style={{
            padding: '4px 10px',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            background:
              value === 'pending'
                ? '#FEF3C7'
                : value === 'completed'
                ? '#D1FAE5'
                : value === 'contacted'
                ? '#DBEAFE'
                : '#FEE2E2',
            color:
              value === 'pending'
                ? '#92400E'
                : value === 'completed'
                ? '#065F46'
                : value === 'contacted'
                ? '#1E40AF'
                : '#991B1B',
            border:
              value === 'pending'
                ? '1px solid #FDE68A'
                : value === 'completed'
                ? '1px solid #A7F3D0'
                : value === 'contacted'
                ? '1px solid #BFDBFE'
                : '1px solid #FCA5A5'
          }}
        >
          {value}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString()
    }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <LoadingSpinner fullScreen />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ color: '#111827', fontSize: '26px', fontWeight: '700', marginBottom: '6px', letterSpacing: '-0.02em' }}>
          Donor Management
        </h1>
        <p style={{ color: '#6B7280', fontSize: '14px', fontWeight: '500' }}>
          View and manage donation submissions. Contact donors to process their contributions.
        </p>
      </div>

      {/* Filter Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '20px',
        borderBottom: '2px solid #E5E7EB',
        paddingBottom: '0'
      }}>
        {['all', 'pending', 'contacted', 'completed'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            style={{
              padding: '10px 20px',
              background: 'transparent',
              border: 'none',
              borderBottom: filter === status ? '3px solid #03A84A' : '3px solid transparent',
              color: filter === status ? '#03A84A' : '#6B7280',
              fontSize: '14px',
              fontWeight: filter === status ? '600' : '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
              textTransform: 'capitalize',
              marginBottom: '-2px'
            }}
            onMouseEnter={(e) => {
              if (filter !== status) {
                e.currentTarget.style.color = '#374151';
              }
            }}
            onMouseLeave={(e) => {
              if (filter !== status) {
                e.currentTarget.style.color = '#6B7280';
              }
            }}
          >
            {status} ({donors.filter(d => status === 'all' || d.status === status).length})
          </button>
        ))}
      </div>

      <GlassCard>
        <DataTable
          columns={columns}
          data={filter === 'all' ? donors : donors.filter(d => d.status === filter)}
          actions={(row) => (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => viewDonor(row)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #BFDBFE',
                  background: '#EFF6FF',
                  color: '#1E40AF',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#1E40AF';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = '#1E40AF';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#EFF6FF';
                  e.currentTarget.style.color = '#1E40AF';
                  e.currentTarget.style.borderColor = '#BFDBFE';
                }}
              >
                View
              </button>
              {(row.status === 'pending' || row.status === 'contacted') && (
                <button
                  onClick={async () => {
                    const newStatus = row.status === 'pending' ? 'contacted' : 'completed';
                    await updateStatus(row.id, newStatus);
                  }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: `1px solid ${row.status === 'pending' ? '#A7F3D0' : '#BFDBFE'}`,
                    background: row.status === 'pending' ? '#C7F9E5' : '#DBEAFE',
                    color: row.status === 'pending' ? '#016C33' : '#1E40AF',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = row.status === 'pending' ? '#03A84A' : '#3B82F6';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.borderColor = row.status === 'pending' ? '#03A84A' : '#3B82F6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = row.status === 'pending' ? '#C7F9E5' : '#DBEAFE';
                    e.currentTarget.style.color = row.status === 'pending' ? '#016C33' : '#1E40AF';
                    e.currentTarget.style.borderColor = row.status === 'pending' ? '#A7F3D0' : '#BFDBFE';
                  }}
                >
                  {row.status === 'pending' ? 'Mark Contacted' : 'Mark Completed'}
                </button>
              )}
            </div>
          )}
        />
      </GlassCard>

      {/* View Donor Modal */}
      {showModal && selectedDonor && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '85vh',
              overflow: 'auto',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'linear-gradient(135deg, #03A84A 0%, #028A3F 100%)'
            }}>
              <h2 style={{ color: 'white', fontSize: '20px', fontWeight: '700', margin: 0 }}>
                Donation Details
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              {/* Donor Information */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ color: '#111827', fontSize: '16px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '20px' }}>👤</span> Donor Information
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '12px', fontSize: '14px' }}>
                  <span style={{ color: '#6B7280', fontWeight: '600' }}>Name:</span>
                  <span style={{ color: '#111827', fontWeight: '500' }}>{selectedDonor.full_name}</span>
                  
                  <span style={{ color: '#6B7280', fontWeight: '600' }}>Email:</span>
                  <a href={`mailto:${selectedDonor.email}`} style={{ color: '#3B82F6', fontWeight: '500', textDecoration: 'none' }}>
                    {selectedDonor.email}
                  </a>
                  
                  {selectedDonor.contact_number && (
                    <>
                      <span style={{ color: '#6B7280', fontWeight: '600' }}>Phone:</span>
                      <a href={`tel:${selectedDonor.contact_number}`} style={{ color: '#3B82F6', fontWeight: '500', textDecoration: 'none' }}>
                        {selectedDonor.contact_number}
                      </a>
                    </>
                  )}
                  
                  <span style={{ color: '#6B7280', fontWeight: '600' }}>Country:</span>
                  <span style={{ color: '#111827', fontWeight: '500' }}>{selectedDonor.country}</span>
                </div>
              </div>

              {/* Donation Details */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ color: '#111827', fontSize: '16px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '20px' }}>💰</span> Donation Details
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '12px', fontSize: '14px' }}>
                  <span style={{ color: '#6B7280', fontWeight: '600' }}>Amount:</span>
                  <span style={{ color: '#03A84A', fontWeight: '700', fontSize: '18px' }}>
                    ${parseFloat(selectedDonor.amount).toFixed(2)}
                  </span>
                  
                  <span style={{ color: '#6B7280', fontWeight: '600' }}>Type:</span>
                  <span style={{ color: '#111827', fontWeight: '500', textTransform: 'capitalize' }}>
                    {selectedDonor.donation_type}
                  </span>
                  
                  <span style={{ color: '#6B7280', fontWeight: '600' }}>Payment Method:</span>
                  <span style={{ color: '#111827', fontWeight: '500', textTransform: 'capitalize' }}>
                    {selectedDonor.payment_method || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Not specified (to be arranged)</span>}
                  </span>
                  
                  <span style={{ color: '#6B7280', fontWeight: '600' }}>Status:</span>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em',
                      background:
                        selectedDonor.status === 'pending'
                          ? '#FEF3C7'
                          : selectedDonor.status === 'completed'
                          ? '#C7F9E5'
                          : '#DBEAFE',
                      color:
                        selectedDonor.status === 'pending'
                          ? '#92400E'
                          : selectedDonor.status === 'completed'
                          ? '#016C33'
                          : '#1E40AF',
                      border:
                        selectedDonor.status === 'pending'
                          ? '1px solid #FDE68A'
                          : selectedDonor.status === 'completed'
                          ? '1px solid #A7F3D0'
                          : '1px solid #BFDBFE'
                    }}
                  >
                    {selectedDonor.status}
                  </span>
                  
                  <span style={{ color: '#6B7280', fontWeight: '600' }}>Submitted:</span>
                  <span style={{ color: '#111827', fontWeight: '500' }}>
                    {new Date(selectedDonor.created_at).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Message */}
              {selectedDonor.message && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ color: '#111827', fontSize: '16px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '20px' }}>💬</span> Message
                  </h3>
                  <div style={{
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    padding: '16px',
                    color: '#374151',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {selectedDonor.message}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                {selectedDonor.status !== 'contacted' && selectedDonor.status !== 'completed' && (
                  <button
                    onClick={async () => {
                      await updateStatus(selectedDonor.id, 'contacted');
                      setSelectedDonor({ ...selectedDonor, status: 'contacted' });
                    }}
                    style={{
                      flex: 1,
                      padding: '12px 20px',
                      background: '#DBEAFE',
                      border: '1px solid #93C5FD',
                      borderRadius: '8px',
                      color: '#1E40AF',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#BFDBFE';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#DBEAFE';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    Mark as Contacted
                  </button>
                )}
                
                <a
                  href={`mailto:${selectedDonor.email}?subject=Thank you for your donation to New Life Nepal&body=Dear ${selectedDonor.full_name},%0D%0A%0D%0AThank you for your generous donation of $${parseFloat(selectedDonor.amount).toFixed(2)}.%0D%0A%0D%0A`}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    background: 'linear-gradient(135deg, #03A84A 0%, #028A3F 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    textDecoration: 'none',
                    textAlign: 'center',
                    display: 'block'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(3, 168, 74, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  📧 Contact via Email
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDonors;
